import { useCallback, useEffect, useRef, useState } from 'react'

import type { ChatUsage } from './types'

export const useChatUsage = (companyId: number, apiUrl: string, token?: string | null) => {
  const [state, setState] = useState<{ scope: string; usage: ChatUsage | null; error: boolean } | null>(null)
  const scope = `${companyId}:${apiUrl}:${token || ''}`
  const currentScope = useRef(scope)
  currentScope.current = scope
  const revision = useRef(0)
  const mounted = useRef(false)

  const updateUsage = useCallback((usage: ChatUsage) => {
    if (!mounted.current || currentScope.current !== scope || usage?.company_id !== companyId) {
      return
    }

    if (typeof usage.unlimited !== 'boolean') {
      return
    }
    
    revision.current += 1
    setState({ scope, usage, error: false })
  }, [companyId, scope])

  const refreshUsage = useCallback(async () => {
    const requestRevision = ++revision.current
    if (!apiUrl || !token) {
      if (mounted.current && currentScope.current === scope) {
        setState({ scope, usage: null, error: true })
      } 

      return
    }
    try {
      const response = await fetch(`${apiUrl}/api/v1/companies/${companyId}/chatbot/usage`, {
        headers: { Authorization: `Bearer ${token}` },
        cache: 'no-store',
      })
      
      if (!response.ok) throw new Error('Usage unavailable')
      const usage: ChatUsage = await response.json()
      
      if (usage.company_id !== companyId || typeof usage.unlimited !== 'boolean') {
        throw new Error('Invalid usage')
      }
      
      if (mounted.current && currentScope.current === scope && revision.current === requestRevision) {
        updateUsage(usage)
      }
    } catch {
      if (mounted.current && currentScope.current === scope && revision.current === requestRevision) {
        setState((previous) => ({ scope, usage: previous?.scope === scope ? previous.usage : null, error: true }))
      }
    }
  }, [apiUrl, companyId, scope, token, updateUsage])

  useEffect(() => {
    mounted.current = true
    refreshUsage()
    const timer = setInterval(refreshUsage, 30000)
    const onFocus = () => { refreshUsage() }
    
    if (typeof window !== 'undefined') {
      window.addEventListener('focus', onFocus)
    } 

    return () => {
      mounted.current = false
      revision.current += 1
      clearInterval(timer)
      if (typeof window !== 'undefined') window.removeEventListener('focus', onFocus)
    }
  }, [refreshUsage])

  const current = state?.scope === scope ? state : null
  
  return { usage: current?.usage ?? null, usageError: current?.error ?? false, updateUsage, refreshUsage }
}
